import * as React from 'react';
import {
    Upload,
    AssignedLocation,
    ViewFileDto,
    t,
    getGeneralMimeType,
    getThumbnailFilePathUsingFileName,
} from '@deltasierra/shared';
import './ResourcePickerModal.scss';
import PanToolOutlined from '@material-ui/icons/PanToolOutlined';
import { Modal, ModalBody, ModalFooter } from '../Modal';
import { Button } from '../Button';

export type MediaType = 'image' | 'video';

const useTranslations = () =>
    React.useMemo(
        () =>
            Object.freeze({
                image: {
                    boldedDisclaimer: t('BUILD.IMAGE_CHOOSER.PERMISSION_DISCLAIMER_STRONG'),
                    chooseFromAssetLibrary: t('BUILD.IMAGE_CHOOSER.SELECT_IMAGE_FROM_YOUR_LIBRARY'),
                    chooseFromDevice: t('BUILD.IMAGE_CHOOSER.UPLOAD_IMAGE_FROM_YOUR_DEVICE'),
                    chooseFromPlanner: t('BUILD.IMAGE_CHOOSER.UPLOADED_PLANNER_IMAGE'),
                    chooseFromSuggestedCollection: t('BUILD.IMAGE_CHOOSER.SUGGESTED_COLLECTION'),
                    chooseResource: t('BUILD.IMAGE_CHOOSER.IMAGE_SOURCE'),
                    disclaimer: t('BUILD.IMAGE_CHOOSER.PERMISSION_DISCLAIMER'),
                },
                video: {
                    boldedDisclaimer: t('BUILD.VIDEO_CHOOSER.PERMISSION_DISCLAIMER_STRONG'),
                    chooseFromAssetLibrary: t('BUILD.VIDEO_CHOOSER.SELECT_VIDEO_FROM_YOUR_LIBRARY'),
                    chooseFromDevice: t('BUILD.VIDEO_CHOOSER.UPLOAD_VIDEO_FROM_YOUR_DEVICE'),
                    chooseFromPlanner: t('BUILD.VIDEO_CHOOSER.UPLOADED_PLANNER_VIDEO'),
                    chooseFromSuggestedCollection: t('BUILD.VIDEO_CHOOSER.SUGGESTED_COLLECTION'),
                    chooseResource: t('BUILD.VIDEO_CHOOSER.VIDEO_SOURCE'),
                    disclaimer: t('BUILD.VIDEO_CHOOSER.PERMISSION_DISCLAIMER'),
                },
            }),
        [],
    );
export type AssetForPicker = Pick<ViewFileDto, 'id' | 'mimeType' | 'url'>;

export type AssetLibraryResourceType = {
    type: 'assetLibrary';
    asset: AssetForPicker;
};

export type PlannerResourceType = {
    type: 'planner';
    upload: Upload;
};

export type LogoResourceType = {
    type: 'logo';
    upload: Upload;
};

export type LocalResourceType = {
    type: 'local';
    file: File;
};

export type AnyResourceType = AssetLibraryResourceType | LocalResourceType | LogoResourceType | PlannerResourceType;

export type DisclaimerBodyProp = {
    boldedDisclaimer: string;
    disclaimer: string;
    confirmStatement: string;
    onConfirm: () => void;
};

export const DisclaimerBody: React.FunctionComponent<DisclaimerBodyProp> = ({
    boldedDisclaimer,
    confirmStatement,
    disclaimer,
    onConfirm,
}) => (
    <div className="disclaimer-body alert-danger">
        <span className="disclaimer-body-icon">
            <PanToolOutlined style={{ fontSize: '36px' }} />
        </span>
        <p>
            <strong>{boldedDisclaimer}</strong>
            <br />
            {disclaimer}
            <span className="disclaimer-body-checkbox-div">
                <span className="disclaimer-body-checkbox-clickable" onClick={() => onConfirm()}>
                    <span>
                        <input className="disclaimer-body-checkbox" name="disclaimer" type="checkbox" />
                    </span>
                    <span className="disclaimer-body-checkbox-text">{confirmStatement}</span>
                </span>
            </span>
        </p>
    </div>
);

DisclaimerBody.displayName = 'DisclaimerBody';

export type ResourcePickerModalOptionProps = {
    text: string;
    icon: string;
    onClick: () => void;
};

export const ResourcePickerModalOption: React.FunctionComponent<ResourcePickerModalOptionProps> = ({
    icon,
    onClick,
    text,
}) => (
    <div className="resource-picker-option" onClick={() => onClick()}>
        <i className={icon} style={{ color: '#c42a86' }} />
        <span className="resource-picker-text">{text}</span>
    </div>
);

ResourcePickerModalOption.displayName = 'ResourcePickerModalOption';

export type ResourcePickerModalFileUploadProps = {
    text: string;
    icon: string;
    onClick: (localResource: LocalResourceType) => void;
};

export const ResourcePickerModalFileUpload: React.FunctionComponent<ResourcePickerModalFileUploadProps> = ({
    icon,
    onClick,
    text,
}) => {
    const handleOnFileInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        if (event.target.files) {
            onClick({ file: event.target.files[0], type: 'local' });
        }
    };

    return (
        <>
            <input
                id="resource-picker-file-upload-input"
                style={{ display: 'none' }}
                type="file"
                onChange={handleOnFileInputChange}
            />
            <label className="resource-picker-file-upload-label" htmlFor="resource-picker-file-upload-input">
                <div className="resource-picker-option">
                    <i className={icon} style={{ color: '#c42a86' }} />
                    <span className="resource-picker-text">{text}</span>
                </div>
            </label>
        </>
    );
};

ResourcePickerModalFileUpload.displayName = 'ResourcePickerModalFileUpload';

export type ResourcePickerModalResourceOptionProps = {
    text: string;
    thumbnail: string;
    filename: string;
    onClick: () => void;
};

export const ResourcePickerModalResourceOption: React.FunctionComponent<ResourcePickerModalResourceOptionProps> = ({
    filename,
    onClick,
    text,
    thumbnail,
}) => (
    <div className="resource-picker-option" onClick={() => onClick()}>
        <div style={{ display: 'inline-block', paddingRight: '20px' }}>
            <img alt={filename} src={thumbnail} style={{ height: '80px', width: '80px' }} />
        </div>
        <span>
            <span className="resource-picker-text">{text}</span>
            <div className="resource-picker-filename">{filename}</div>
        </span>
    </div>
);

ResourcePickerModalResourceOption.displayName = 'ResourcePickerModalResourceOption';

export type ResourcePickerModalResourceDisplayProps = {
    media: MediaType;
    plannerResources?: Upload[];
    assetResources?: ViewFileDto[];
    onChoose: (resource: AnyResourceType) => void;
};

export const ResourcePickerModalResourceDisplay: React.FunctionComponent<ResourcePickerModalResourceDisplayProps> = ({
    assetResources,
    media,
    onChoose,
    plannerResources,
}) => {
    const translations = useTranslations();
    return (
        <>
            {plannerResources &&
                plannerResources
                    .filter(upload => getGeneralMimeType(upload.filename) === media)
                    .map(upload => (
                        <ResourcePickerModalResourceOption
                            filename={upload.filename}
                            key={upload.url as string}
                            text={translations[media].chooseFromPlanner}
                            thumbnail={upload.thumb256x256url as string}
                            onClick={() => onChoose({ type: 'planner', upload })}
                        />
                    ))}
            {media === 'image' &&
                assetResources &&
                assetResources
                    .filter(asset => asset.type === media)
                    .map(asset => (
                        <ResourcePickerModalResourceOption
                            filename={asset.title}
                            key={asset.id}
                            text={translations[media].chooseFromAssetLibrary}
                            thumbnail={
                                asset.thumbnails.medium
                                    ? asset.thumbnails.medium.url
                                    : getThumbnailFilePathUsingFileName(asset.title)
                            }
                            onClick={() => onChoose({ asset, type: 'assetLibrary' })}
                        />
                    ))}
        </>
    );
};

ResourcePickerModalResourceDisplay.displayName = 'ResourcePickerModalResourceDisplay';

export type ResourcePickerModalProps = {
    location: AssignedLocation;
    media: MediaType;
    show: boolean;
    canChooseLocationLogo: boolean;
    plannerResources?: Upload[];
    assetResources?: ViewFileDto[];
    hasSuggestedCollection?: boolean;
    onChoose: (resource: AnyResourceType) => void;
    onClickSelectFromAssetLibrary: () => void;
    onClickSelectFromSuggestedCollection?: () => void;
    onClose?: () => void;
};

export const ResourcePickerModal: React.FunctionComponent<ResourcePickerModalProps> = ({
    assetResources,
    canChooseLocationLogo,
    hasSuggestedCollection = false,
    location,
    media,
    onChoose,
    onClickSelectFromAssetLibrary,
    onClickSelectFromSuggestedCollection = () => null,
    onClose,
    plannerResources,
    show,
}) => {
    const [checkedPermission, setCheckedPermission] = React.useState(false);
    const translations = useTranslations();

    React.useEffect(() => {
        if (!show && checkedPermission) {
            setCheckedPermission(false);
        }
    }, [show, checkedPermission]);

    return (
        <Modal closeOnBackdropClick={false} show={show} onClose={onClose}>
            <div className="resource-picker-header branded-alternate">
                <h3 className="modal-title">
                    <p>{translations[media].chooseResource}</p>
                </h3>
            </div>
            <ModalBody data-cy="resource-picker-modal-body">
                {!checkedPermission && (
                    <DisclaimerBody
                        boldedDisclaimer={translations[media].boldedDisclaimer}
                        confirmStatement={t('BUILD.IMAGE_CHOOSER.CONFIRM_PERMISSION')}
                        disclaimer={translations[media].disclaimer}
                        onConfirm={() => setCheckedPermission(true)}
                    />
                )}
                {checkedPermission && (
                    <div className="resource-picker-body-scroll">
                        <div style={{ borderBottom: '1px solid lightgrey' }}>
                            <ResourcePickerModalResourceDisplay
                                assetResources={assetResources}
                                media={media}
                                plannerResources={plannerResources}
                                onChoose={onChoose}
                            />
                            <ResourcePickerModalFileUpload
                                icon="glyphicon glyphicon-open"
                                text={translations[media].chooseFromDevice}
                                onClick={resource => onChoose(resource)}
                            />
                            <ResourcePickerModalOption
                                icon="glyphicon glyphicon-picture"
                                text={translations[media].chooseFromAssetLibrary}
                                onClick={onClickSelectFromAssetLibrary}
                            />
                            {hasSuggestedCollection && (
                                <ResourcePickerModalOption
                                    icon="glyphicon glyphicon-folder-open"
                                    text={translations[media].chooseFromSuggestedCollection}
                                    onClick={onClickSelectFromSuggestedCollection}
                                />
                            )}
                            {media === 'image' && location.logo && canChooseLocationLogo && (
                                <ResourcePickerModalOption
                                    icon="glyphicon glyphicon-paperclip"
                                    text={t('BUILD.IMAGE_CHOOSER.LOCATION_LOGO')}
                                    onClick={() => onChoose({ type: 'logo', upload: location.logo! })}
                                />
                            )}
                        </div>
                    </div>
                )}
            </ModalBody>
            <ModalFooter>
                <Button onClick={onClose}>{t('COMMON.CANCEL')}</Button>
            </ModalFooter>
        </Modal>
    );
};

ResourcePickerModal.displayName = 'ResourcePickerModal';
